import axios from 'axios';
import type { AxiosError, HeadersDefaults } from 'axios';
import AuthService from '@/services/auth.service';

// __BFF_BASE__ lo inyecta vite (define) según el build:
//  - build integrado (EC2, vite.config.ts): '/'  -> mismo origen, pega directo
//    a los endpoints (sin el salto 307 del puente /bff).
//  - build Amplify (vite.config.amplify.ts): '/bff/' -> Amplify reescribe
//    /bff/<*> al backend (clients.cymasuite.com), manteniendo same-origin.
const http = axios.create({
  baseURL: __BFF_BASE__,
  // 30s timeout: protege de Puma colgado / red lenta. Bajo este umbral el catch
  // recibe un AxiosError con `code === 'ECONNABORTED'` y la UI muestra error en
  // lugar de spinner eterno.
  timeout: 30_000,
  // Debe ser true para que el navegador GUARDE los Set-Cookie del login
  // (sesión por cookies). El CORS del backend es allowlist con
  // Access-Control-Allow-Credentials: true + origenes *.cymasuite.com, así que
  // las requests credenciadas (incluido el hop cross-origin en Amplify) pasan.
  withCredentials: true,
});

http.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    // axios routes 401 here (not to the success handler), so the session
    // cleanup must live in this branch. Skip it for the auth endpoints
    // themselves, so a wrong-password login stays on the form with its error
    // instead of being redirected away.
    const url: string = error?.config?.url || '';
    const isAuthRequest =
      url.includes('users/login') || url.includes('users/signup');

    if (error?.response?.status === 401 && !isAuthRequest) {
      AuthService.clearCache();
    }

    // Reject con el AxiosError ORIGINAL (antes se aplanaba a string vía
    // `error?.response?.data || 'Unknown Error'`, lo que rompía a los consumers
    // que hacen `error?.response?.data` y perdía el status 422 vs 500). Ahora los
    // consumers reciben axios shape: `err.response.status`, `err.response.data`,
    // `err.code` (ECONNABORTED para timeout), `err.request` (para network error).
    return Promise.reject(error);
  }
);

// Helper para extraer mensaje legible del AxiosError. Soporta backend que devuelve
// string, hash { field: [msg, ...] } (422 estándar Rails) o { error: "..." }.
export function extractErrorMessage(
  err: unknown,
  fallback = 'No se pudo completar la operación.',
): string {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const e = err as any;
  if (e?.code === 'ECONNABORTED') {
    return 'La solicitud demoró más de lo esperado. Reintentá.';
  }
  if (e?.request && !e?.response) {
    return 'Sin conexión con el servidor.';
  }
  const data = e?.response?.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (data && typeof data === 'object') {
    const fieldMsgs = Object.values(data).flat().filter(Boolean) as string[];
    if (fieldMsgs.length) return fieldMsgs.join(' · ');
    if (data.error) return String(data.error);
    if (data.message) return String(data.message);
    if (data.motivo) return String(data.motivo);
  }
  return e?.message || fallback;
}

const setHTTPHeader = (header: HeadersDefaults['common']) => {
  http.defaults.headers.common = { ...http.defaults.headers.common, ...header };
};

export { http, setHTTPHeader };
